import React, { useEffect, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { format } from 'date-fns';
import { Clock, Download, Eye, Star } from 'lucide-react';
import toast from 'react-hot-toast';

import PageHeader from '../../components/layout/PageHeader';
import Badge from '../../components/ui/Badge';
import DocumentPreviewModal, {
  type PreviewContentKind,
} from '../../components/ui/DocumentPreviewModal';
import Table, { type TableColumn } from '../../components/ui/Table';
import favoritesService from '../../services/favoritesService';
import documentService from '../../services/document.service';
import axiosInstance from '../../lib/axios';
// import { formatDocumentDisplayName } from '../../utils/documentName';
import {
  canDownloadDocument,
  canPreviewDocument,
} from '../../utils/documentAccessMode';
import type { LatestFile } from '../../types/favorites.types';

const PAGE_SIZE = 10;

const LatestFilesPage: React.FC = () => {
  const queryClient = useQueryClient();
  const [page, setPage] = useState(1);
  const [previewDocId, setPreviewDocId] = useState<number | string | null>(
    null
  );
  const [previewContentKind, setPreviewContentKind] =
    useState<PreviewContentKind | null>(null);

  const handleFavoriteToggle = async (documentId: number | string, isFavorite: boolean | undefined) => {
    try {
      if (isFavorite) {
        await axiosInstance.delete(`/documents/${documentId}/favorite`);
        toast.success('Removed from favorites');
      } else {
        await axiosInstance.post(`/documents/${documentId}/favorite`);
        toast.success('Added to favorites');
      }
      queryClient.invalidateQueries({ queryKey: ['latest-files'] });
    } catch {
      toast.error('Failed to update favorites');
    }
  };

  useEffect(() => {
    document.title = 'RRMS | Latest Files';
  }, []);

  const latestFilesQuery = useQuery({
    queryKey: ['latest-files', page],
    queryFn: () => favoritesService.getLatestFiles({ page, limit: PAGE_SIZE }),
  });

  const data = latestFilesQuery.data;
  const items: LatestFile[] = data?.data ?? [];
  const pagination =
    data?.pagination ?? {
      page,
      limit: PAGE_SIZE,
      total: items.length,
      totalPages: 1,
    };

  const isLoading =
    latestFilesQuery.isLoading || latestFilesQuery.isFetching;
  const isError = latestFilesQuery.isError;

  const columns: TableColumn<LatestFile>[] = [
    // {
    //   key: 'file_name',
    //   header: 'File Name',
    //   minWidth: 240,
    //   clampLines: 2,
    //   tooltip: (doc) =>
    //     formatDocumentDisplayName({
    //       cmsNumber: doc.cms_number,
    //       originalFileName: doc.file_name,
    //       documentType: doc.document_type,
    //     }),
    //   render: (doc) => {
    //     const previewAllowedByType = canPreviewDocument({ fileName: doc.file_name });

    //     return previewAllowedByType ? (
    //       <button
    //         type="button"
    //         onClick={() => setPreviewDocId(doc.document_id)}
    //         style={{ background: 'none', border: 'none', cursor: 'pointer', fontWeight: 600, color: 'var(--color-primary)', fontSize: 'inherit', textAlign: 'left', padding: 0 }}
    //       >
    //         {formatDocumentDisplayName({
    //           cmsNumber: doc.cms_number,
    //           originalFileName: doc.file_name,
    //           documentType: doc.document_type,
    //         })}
    //       </button>
    //     ) : (
    //       <span>
    //         {formatDocumentDisplayName({
    //           cmsNumber: doc.cms_number,
    //           originalFileName: doc.file_name,
    //           documentType: doc.document_type,
    //         })}
    //       </span>
    //     );
    //   },
    // },
    {
      key: 'file_name',
      header: 'File Name',
      tooltip: (doc) => doc.file_name,
      render: (doc) => {
        const previewAllowedByType = canPreviewDocument({ fileName: doc.file_name });
        return previewAllowedByType ? (
          <button
            type="button"
            onClick={() => setPreviewDocId(doc.document_id)}
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              fontWeight: 600,
              color: 'var(--color-primary)',
              fontSize: 'inherit',
              textAlign: 'left',
              padding: 0,
            }}
          >
            {doc.file_name}
          </button>
        ) : (
          <span>{doc.file_name}</span>
        );
      },
    },
    {
      key: 'file_number',
      header: 'File No',
      minWidth: 160,
      tooltip: (doc) =>
        `${(doc as any).file_number || doc.cms_number || '—'}${doc.rrms_ref_number ? ` | ${doc.rrms_ref_number}` : ''}`,
      render: (doc) => (
        <>
          <span style={{ fontFamily: 'var(--font-family-mono)', fontSize: 'var(--font-size-sm)' }}>
            {(doc as any).file_number || doc.cms_number || '—'}
          </span>
          <br />
          <span style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-gray-500)' }}>
            {doc.rrms_ref_number || '—'}
          </span>
        </>
      ),
    },
    {
      key: 'hierarchy_path',
      header: 'Section',
      minWidth: 180,
      clampLines: 2,
      tooltip: (doc) => (doc as any).hierarchy_path || doc.ps_name || '—',
      render: (doc) => (doc as any).hierarchy_path || doc.ps_name || '—',
    },
    {
      key: 'category',
      header: 'Category',
      minWidth: 120,
      clampLines: 2,
      tooltip: (doc) => (doc as any).category || '—',
      render: (doc) => (doc as any).category || '—',
    },
    {
      key: 'subject',
      header: 'Subject',
      minWidth: 180,
      clampLines: 2,
      tooltip: (doc) => (doc as any).subject || '—',
      render: (doc) => (doc as any).subject || '—',
    },
    {
      key: 'classification',
      header: 'Classification',
      minWidth: 140,
      tooltip: (doc) => doc.classification,
      render: (doc) => (
        <Badge color={doc.classification === 'Confidential' ? 'error' : 'success'}>
          {doc.classification}
        </Badge>
      ),
    },
    {
      key: 'approval_status',
      header: 'Approval Status',
      minWidth: 140,
      tooltip: (doc) => doc.approval_status,
      render: (doc) => (
        <Badge
          color={
            doc.approval_status === 'Pending'
              ? 'warning'
              : doc.approval_status === 'Approved'
              ? 'success'
              : 'error'
          }
        >
          {doc.approval_status}
        </Badge>
      ),
    },
    {
      key: 'uploaded_at',
      header: 'Date Uploaded',
      minWidth: 170,
      tooltip: (doc) =>
        doc.uploaded_at ? format(new Date(doc.uploaded_at), 'dd MMM yyyy, HH:mm') : '—',
      render: (doc) =>
        doc.uploaded_at ? format(new Date(doc.uploaded_at), 'dd MMM yyyy, HH:mm') : '—',
    },
    {
      key: 'actions',
      header: 'Actions',
      minWidth: 140,
      tooltip: () => 'Preview, download, or favorite',
      render: (doc) => {
        const previewAllowedByType = canPreviewDocument({ fileName: doc.file_name });
        const downloadAllowedByType = canDownloadDocument({ fileName: doc.file_name });
        const hideDownloadWhilePdfOrImagePreview =
          previewDocId != null &&
          (previewContentKind === 'pdf' || previewContentKind === 'image') &&
          String(doc.document_id) === String(previewDocId);

        return (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            {previewAllowedByType && (
              <button
                type="button"
                onClick={() => setPreviewDocId(doc.document_id)}
                title="Preview"
                style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 4, color: 'var(--color-primary)', display: 'inline-flex', alignItems: 'center' }}
              >
                <Eye size={16} />
              </button>
            )}
            {downloadAllowedByType && !hideDownloadWhilePdfOrImagePreview && (
              <button
                type="button"
                onClick={() =>
                  // documentService
                  //   .downloadDocument(
                  //     String(doc.document_id),
                  //     formatDocumentDisplayName({
                  //       cmsNumber: doc.cms_number,
                  //       originalFileName: doc.file_name,
                  //       documentType: doc.document_type,
                  //     })
                  //   )
                  //   .catch(() => toast.error('Download failed'))
                  documentService
                    .downloadDocument(
                      String(doc.document_id),
                      doc.file_name
                    )
                    .catch(() => toast.error('Download failed'))
                }
                title="Download"
                style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 4, color: 'var(--color-primary)', display: 'inline-flex', alignItems: 'center' }}
              >
                <Download size={16} />
              </button>
            )}
            <button
              type="button"
              onClick={() => handleFavoriteToggle(doc.document_id, (doc as any).is_favorite)}
              title={(doc as any).is_favorite ? 'Remove from favorites' : 'Add to favorites'}
              style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 4, display: 'inline-flex', alignItems: 'center' }}
            >
              <Star
                size={16}
                fill={(doc as any).is_favorite ? 'var(--color-accent)' : 'none'}
                color={(doc as any).is_favorite ? 'var(--color-accent)' : 'var(--color-gray-400)'}
              />
            </button>
          </div>
        );
      },
    },
  ];

  return (
    <div className="">
      <PageHeader
        title="Latest Files"
        subtitle="Recently uploaded documents"
      />

      {isLoading && (
        <div className="card p-4">
          {Array.from({ length: 5 }).map((_, i) => (
            <div key={i} className="h-12 animate-pulse rounded bg-[var(--color-gray-100)]" style={{ marginBottom: i === 4 ? 0 : 'var(--spacing-3)' }} />
          ))}
        </div>
      )}

      {!isLoading && isError && (
        <div className="card p-8 text-center">
          <p className="text-md text-[var(--color-error)]">
            Failed to load latest files. Please try again.
          </p>
        </div>
      )}

      {!isLoading && !isError && items.length === 0 && (
        <div className="card p-10 text-center">
          <Clock
            size={40}
            className="mx-auto mb-4 text-[var(--color-gray-300)]"
          />
          <p className="text-md font-semibold text-[var(--color-gray-700)]">
            No uploaded documents yet.
          </p>
          <p className="mt-1 text-sm text-muted">
            Recently uploaded documents will appear here.
          </p>
        </div>
      )}

      {!isLoading && !isError && items.length > 0 && (
        <Table
          columns={columns}
          rows={items}
          rowKey={(doc) => String(doc.document_id)}
          pagination={pagination}
          onPageChange={setPage}
          minWidth={900}
        />
      )}

      <DocumentPreviewModal
        documentId={previewDocId}
        onClose={() => {
          setPreviewDocId(null);
          setPreviewContentKind(null);
        }}
        onPreviewContentKind={setPreviewContentKind}
      />
    </div>
  );
};

export default LatestFilesPage;
